import React, { useState, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ProjectSelector } from '@/components/ProjectSelector';
import { CostCentreSelector } from '@/components/CostCentreSelector';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { TimesheetEntry, DayKey, Project, CostCentre } from '@/types/timesheet';
import type { OvertimeType } from '@/types/timesheetSettings';

// ─── Types ────────────────────────────────────────────────────────────────────

/** The per-day table row data used internally by the modal */
interface DayRow {
  day: DayKey;
  label: string;
  totalHours: number;
  otHours: string; // controlled string so the user can type freely
  remainingHours: number;
  isValid: boolean;
  errorMessage?: string;
}

/** What the modal produces on confirm */
export interface OvertimeClassificationResult {
  /** The original entry, updated with standard-hours per day (total − OT) */
  updatedEntry: TimesheetEntry;
  /**
   * New row carrying only the OT hours and the selected work type.
   * Days where OT hours === 0 are set to 0 (not omitted) so the row shape stays
   * consistent with TimesheetEntry; the caller may choose to strip them.
   */
  newOvertimeEntry: TimesheetEntry;
}

export interface OvertimeClassificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The timesheet row being classified */
  entry: TimesheetEntry;
  /** The ordered list of visible days (as driven by weekStartDay & working days config) */
  visibleDays: DayKey[];
  /** Labels for the visible days in the same order (e.g. ['Mon', 'Tue', …]) */
  dayLabels: string[];
  /** Active overtime types available to select */
  overtimeTypes: OvertimeType[];
  /** Pre-selected work type id (e.g. org default) */
  defaultOvertimeTypeId?: number;
  /**
   * Per-day suggested OT hours — pre-populates the input fields.
   * Supplied by the AI Insights CTA on contracted-hours-exceeded warnings.
   */
  suggestedOvertimeHours?: Partial<Record<DayKey, number>>;
  /** Called with both the updated original row and the new OT row on confirm */
  onConfirm: (result: OvertimeClassificationResult) => void;
  /** Next available entry id — used to generate the new OT row's id */
  nextEntryId: number;
  /** Available projects for resolving IDs to friendly names and for the override selector */
  projects: Project[];
  /** Available cost centres for resolving IDs to friendly names and for the override selector */
  costCentres: CostCentre[];
  /** Server-side project search callback */
  onServerSearch?: (query: string) => void;
  /** Whether a server-side project search is in progress */
  isSearchingProjects?: boolean;
  /** Company ID to scope favourites per company */
  companyId?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatHours(h: number): string {
  return h % 1 === 0 ? `${h}` : h.toFixed(2);
}

function parseOtInput(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed === '0') return 0;
  const parsed = parseFloat(trimmed);
  return isNaN(parsed) ? NaN : parsed;
}

function validateOtHours(
  raw: string,
  totalHours: number,
): { value: number; isValid: boolean; errorMessage?: string } {
  const value = parseOtInput(raw);
  if (isNaN(value)) {
    return { value: 0, isValid: false, errorMessage: 'Must be a number' };
  }
  if (value < 0) {
    return { value, isValid: false, errorMessage: 'Cannot be negative' };
  }
  if (value > totalHours) {
    return {
      value,
      isValid: false,
      errorMessage: `Cannot exceed ${formatHours(totalHours)}h logged`,
    };
  }
  return { value, isValid: true };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OvertimeClassificationModal({
  open,
  onOpenChange,
  entry,
  visibleDays,
  dayLabels,
  overtimeTypes,
  defaultOvertimeTypeId,
  suggestedOvertimeHours,
  onConfirm,
  nextEntryId,
  projects,
  costCentres,
  onServerSearch,
  isSearchingProjects,
  companyId,
}: OvertimeClassificationModalProps) {
  // ── Work type selection ────────────────────────────────────────────────────
  const [selectedOvertimeTypeId, setSelectedOvertimeTypeId] = useState<string>(
    defaultOvertimeTypeId != null ? String(defaultOvertimeTypeId) : overtimeTypes[0]?.id != null ? String(overtimeTypes[0].id) : '',
  );

  // Reset work type when the modal opens (in case the prop changes between opens)
  React.useEffect(() => {
    if (open) {
      setSelectedOvertimeTypeId(defaultOvertimeTypeId != null ? String(defaultOvertimeTypeId) : overtimeTypes[0]?.id != null ? String(overtimeTypes[0].id) : '');
    }
  }, [open, defaultOvertimeTypeId, overtimeTypes]);

  // ── Per-day OT inputs ─────────────────────────────────────────────────────
  const initialOtInputs = useMemo<Record<DayKey, string>>(() => {
    const result = {} as Record<DayKey, string>;
    visibleDays.forEach((day) => {
      const suggested = suggestedOvertimeHours?.[day];
      const total = entry.hours[day] ?? 0;
      // Only pre-populate if the suggestion is meaningful (> 0 and within total)
      if (suggested !== undefined && suggested > 0 && suggested <= total) {
        result[day] = formatHours(suggested);
      } else {
        result[day] = '';
      }
    });
    return result;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps — intentionally re-init on open

  const [otInputs, setOtInputs] = useState<Record<DayKey, string>>(initialOtInputs);

  // Re-initialise inputs when the modal (re-)opens
  React.useEffect(() => {
    if (open) {
      setOtInputs(initialOtInputs);
    }
  }, [open, initialOtInputs]);

  // ── Project/cost centre override (collapsed by default) ───────────────────
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideProjectId, setOverrideProjectId] = useState<string>(entry.projectId);
  const [overrideCostCentreId, setOverrideCostCentreId] = useState<string>(entry.costCentreId);

  React.useEffect(() => {
    if (open) {
      setOverrideOpen(false);
      setOverrideProjectId(entry.projectId);
      setOverrideCostCentreId(entry.costCentreId);
    }
  }, [open, entry.projectId, entry.costCentreId]);

  // ── Derived day rows ──────────────────────────────────────────────────────
  const dayRows = useMemo<DayRow[]>(() => {
    return visibleDays.map((day, i) => {
      const total = entry.hours[day] ?? 0;
      const raw = otInputs[day] ?? '';
      const { value, isValid, errorMessage } = validateOtHours(raw, total);
      const otValue = isNaN(value) ? 0 : value;
      return {
        day,
        label: dayLabels[i] ?? day,
        totalHours: total,
        otHours: raw,
        remainingHours: Math.max(0, total - otValue),
        isValid: raw === '' ? true : isValid, // empty = 0 OT, always valid
        errorMessage: raw === '' ? undefined : errorMessage,
      };
    });
  }, [visibleDays, dayLabels, entry.hours, otInputs]);

  // Only show rows where the employee logged hours (rows with 0 hours have nothing to classify)
  const activeDayRows = useMemo(
    () => dayRows.filter((r) => r.totalHours > 0),
    [dayRows],
  );

  // ── Totals ────────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    const loggedTotal = activeDayRows.reduce((s, r) => s + r.totalHours, 0);
    const otTotal = activeDayRows.reduce((s, r) => {
      const parsed = parseOtInput(r.otHours);
      return s + (isNaN(parsed) ? 0 : parsed);
    }, 0);
    const standardTotal = loggedTotal - otTotal;
    return { loggedTotal, otTotal, standardTotal };
  }, [activeDayRows]);

  // ── Validation ────────────────────────────────────────────────────────────
  const allInputsValid = useMemo(
    () => dayRows.every((r) => r.isValid),
    [dayRows],
  );

  const hasAnyOtHours = useMemo(
    () =>
      activeDayRows.some((r) => {
        const v = parseOtInput(r.otHours);
        return !isNaN(v) && v > 0;
      }),
    [activeDayRows],
  );

  const selectedOvertimeType = useMemo(
    () => overtimeTypes.find((t) => String(t.id) === selectedOvertimeTypeId),
    [overtimeTypes, selectedOvertimeTypeId],
  );

  const canConfirm =
    allInputsValid &&
    hasAnyOtHours &&
    !!selectedOvertimeTypeId &&
    !!selectedOvertimeType;

  // ── Input handler ─────────────────────────────────────────────────────────
  const handleOtInput = useCallback((day: DayKey, raw: string) => {
    setOtInputs((prev) => ({ ...prev, [day]: raw }));
  }, []);

  // ── Confirm ───────────────────────────────────────────────────────────────
  const handleConfirm = () => {
    if (!canConfirm || !selectedOvertimeType) return;

    // Build per-day standard and OT hours
    const standardHours: TimesheetEntry['hours'] = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    };
    const overtimeHours: TimesheetEntry['hours'] = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    };

    // For days in visibleDays: split between standard and OT
    visibleDays.forEach((day) => {
      const total = entry.hours[day] ?? 0;
      const raw = otInputs[day] ?? '';
      const parsed = parseOtInput(raw);
      const ot = !isNaN(parsed) && parsed > 0 ? Math.min(parsed, total) : 0;
      overtimeHours[day] = ot;
      standardHours[day] = total - ot;
    });

    // For days NOT in visibleDays (non-working days hidden from the grid), keep on original row
    const allDays: DayKey[] = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];
    allDays.forEach((day) => {
      if (!visibleDays.includes(day)) {
        standardHours[day] = entry.hours[day] ?? 0;
        // No OT for hidden days — they never appeared in the modal
        overtimeHours[day] = 0;
      }
    });

    const updatedEntry: TimesheetEntry = {
      ...entry,
      hours: standardHours,
      // Remove any existing work-type classification from the standard row
      overtimeTypeId: undefined,
      overtimeTypeName: undefined,
      overtimeCostMultiplier: undefined,
      overtimeBillMultiplier: undefined,
    };

    const effectiveProjectId = overrideProjectId || entry.projectId;
    const effectiveCostCentreId = overrideCostCentreId || entry.costCentreId;
    const overrideProject = projects.find((p) => p.id === effectiveProjectId);
    const overrideCostCentre = costCentres.find((cc) => cc.id === effectiveCostCentreId);

    const newOvertimeEntry: TimesheetEntry = {
      ...entry,
      id: nextEntryId,
      hours: overtimeHours,
      projectId: effectiveProjectId,
      projectCode: overrideProject?.code ?? entry.projectCode,
      projectName: overrideProject?.name ?? entry.projectName,
      costCentreId: effectiveCostCentreId,
      costCentreCode: overrideCostCentre?.code ?? entry.costCentreCode,
      costCentreName: overrideCostCentre?.name ?? entry.costCentreName,
      overtimeTypeId: selectedOvertimeType.id,
      overtimeTypeName: selectedOvertimeType.name,
      overtimeCostMultiplier: selectedOvertimeType.costMultiplier,
      overtimeBillMultiplier: selectedOvertimeType.billMultiplier,
      // Clear scheduling / absence metadata from the new row — it's a plain manual entry
      isFromSchedule: false,
      scheduledBookingId: undefined,
      scheduledHours: undefined,
      isDismissed: undefined,
      dismissedAt: undefined,
      dismissalReason: undefined,
      bookingCreatedBy: undefined,
      bookingCreatedByName: undefined,
      bookingNotes: undefined,
      bookingCreatedAt: undefined,
      sourceIntegration: undefined,
      isReadOnly: false,
      isNonProjectTime: entry.isNonProjectTime,
      absenceType: undefined,
      absenceReason: undefined,
      approvedBy: undefined,
      approvedAt: undefined,
      absenceId: undefined,
      absenceRequestId: undefined,
    };

    onConfirm({ updatedEntry, newOvertimeEntry });
    onOpenChange(false);
  };

  // ── No visible days / entry has no hours — show empty state ───────────────
  const entryHasNoHours = activeDayRows.length === 0;

  // ── Display names ─────────────────────────────────────────────────────────
  const resolvedProject = useMemo(
    () => projects.find((p) => p.id === entry.projectId),
    [projects, entry.projectId],
  );
  const projectDisplayName =
    entry.projectName ??
    resolvedProject?.name ??
    entry.projectCode ??
    resolvedProject?.code ??
    entry.projectId;


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Classify Overtime</DialogTitle>
          <DialogDescription>
            Enter the overtime hours for each day on{' '}
            <span className="font-medium text-foreground">{projectDisplayName}</span>. The
            remaining hours will stay on the original row as standard time.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
        <div className="space-y-5">
          {/* ── Work type selector ─────────────────────────────────────────── */}
          <div className="space-y-1.5">
            <Label htmlFor="ot-work-type">Overtime work type</Label>
            {overtimeTypes.length === 0 ? (
              <p className="text-sm text-destructive">
                No overtime types configured. Add one in Settings → Overtime.
              </p>
            ) : (
              <Select
                value={selectedOvertimeTypeId}
                onValueChange={setSelectedOvertimeTypeId}
              >
                <SelectTrigger id="ot-work-type" className="w-full sm:w-[260px]">
                  {selectedOvertimeType ? (
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="h-3 w-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: selectedOvertimeType.badgeColour }}
                      />
                      <span className="truncate">{selectedOvertimeType.name}</span>
                      {selectedOvertimeType.costMultiplier !== undefined && (
                        <span className="ml-1 text-xs text-muted-foreground flex-shrink-0">
                          ×{selectedOvertimeType.costMultiplier}
                        </span>
                      )}
                    </div>
                  ) : (
                    <SelectValue placeholder="Select work type" />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {overtimeTypes.map((ot) => (
                    <SelectItem key={ot.id} value={String(ot.id)}>
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: ot.badgeColour }}
                        />
                        <span>{ot.name}</span>
                        {ot.costMultiplier !== undefined && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            ×{ot.costMultiplier}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* ── Per-day table ─────────────────────────────────────────────── */}
          {entryHasNoHours ? (
            <div className="flex items-center gap-2 rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>This row has no hours logged for the visible days.</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 pr-3 text-left font-medium text-muted-foreground w-[72px]">
                      Day
                    </th>
                    <th className="py-2 px-2 text-right font-medium text-muted-foreground w-[90px]">
                      Logged
                    </th>
                    <th className="py-2 px-2 text-right font-medium w-[110px]">
                      OT hours
                    </th>
                    <th className="py-2 pl-2 text-right font-medium text-muted-foreground w-[110px]">
                      Standard
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {activeDayRows.map((row) => (
                    <tr
                      key={row.day}
                      className={cn(
                        'group',
                        !row.isValid && 'bg-destructive/5',
                      )}
                    >
                      {/* Day label */}
                      <td className="py-2 pr-3 font-medium">{row.label}</td>

                      {/* Total logged (read-only) */}
                      <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">
                        {formatHours(row.totalHours)}h
                      </td>

                      {/* OT hours input */}
                      <td className="py-1.5 px-2">
                        <div className="flex flex-col items-end gap-0.5">
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={row.otHours}
                            onChange={(e) => handleOtInput(row.day, e.target.value)}
                            onFocus={(e) => e.target.select()}
                            placeholder="0"
                            aria-label={`OT hours for ${row.label}`}
                            aria-invalid={!row.isValid}
                            className={cn(
                              'h-8 w-[90px] text-right tabular-nums',
                              !row.isValid &&
                                'border-destructive focus-visible:ring-destructive/50',
                            )}
                          />
                          {row.errorMessage && (
                            <span className="text-[11px] text-destructive leading-none">
                              {row.errorMessage}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Remaining standard hours (live calculated) */}
                      <td
                        className={cn(
                          'py-2 pl-2 text-right tabular-nums',
                          row.remainingHours < 0
                            ? 'text-destructive'
                            : 'text-muted-foreground',
                        )}
                      >
                        {formatHours(row.remainingHours)}h
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Totals footer */}
                <tfoot>
                  <tr className="border-t font-medium">
                    <td className="pt-2 pr-3 text-muted-foreground text-xs uppercase tracking-wide">
                      Total
                    </td>
                    <td className="pt-2 px-2 text-right tabular-nums text-muted-foreground">
                      {formatHours(totals.loggedTotal)}h
                    </td>
                    <td className="pt-2 px-2 text-right tabular-nums">
                      {formatHours(totals.otTotal)}h
                    </td>
                    <td className="pt-2 pl-2 text-right tabular-nums text-muted-foreground">
                      {formatHours(totals.standardTotal)}h
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* ── What will happen summary ───────────────────────────────────── */}
          {hasAnyOtHours && allInputsValid && selectedOvertimeType && (
            <div className="rounded-md bg-muted/50 border px-4 py-3 text-sm space-y-1">
              <p className="font-medium">What will happen on confirm</p>
              <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                <li>
                  Original row updated to{' '}
                  <span className="font-medium text-foreground">
                    {formatHours(totals.standardTotal)}h
                  </span>{' '}
                  standard time
                </li>
                <li>
                  New row created with{' '}
                  <span className="font-medium text-foreground">
                    {formatHours(totals.otTotal)}h
                  </span>{' '}
                  classified as{' '}
                  <span
                    className="inline-flex items-center gap-1 font-medium"
                    style={{ color: selectedOvertimeType.badgeColour }}
                  >
                    <span
                      className="h-2 w-2 rounded-full inline-block"
                      style={{ backgroundColor: selectedOvertimeType.badgeColour }}
                    />
                    {selectedOvertimeType.name}
                  </span>
                </li>
                <li>Days with no OT hours entered are left untouched</li>
              </ul>
            </div>
          )}

          {/* ── Optional project / cost centre override (collapsed) ────────── */}
          <Collapsible open={overrideOpen} onOpenChange={setOverrideOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {overrideOpen ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
                Override project / cost centre for overtime row
                <span className="text-xs opacity-60">(optional)</span>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3 space-y-3 pl-5">
              <p className="text-xs text-muted-foreground">
                By default the new overtime row inherits the same project and cost centre. Change
                these if your overtime should be booked to a different code.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Project</Label>
                  <ProjectSelector
                    projects={projects}
                    value={overrideProjectId}
                    onChange={setOverrideProjectId}
                    placeholder="Select project..."
                    onServerSearch={onServerSearch}
                    isSearching={isSearchingProjects}
                    companyId={companyId}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Cost Centre</Label>
                  <CostCentreSelector
                    costCentres={costCentres}
                    value={overrideCostCentreId}
                    onChange={setOverrideCostCentreId}
                    placeholder="Select cost centre..."
                  />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
        </div>

        <DialogFooter className="shrink-0 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            title={
              !hasAnyOtHours
                ? 'Enter at least one day with OT hours'
                : !allInputsValid
                  ? 'Fix the validation errors above'
                  : !selectedOvertimeTypeId
                    ? 'Select a work type'
                    : undefined
            }
          >
            Classify overtime
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
